// Shared basemap tile layer for every Leaflet map in the app (public map,
// admin map, scan-project workspace, scan-projects overview).
//
// Stadia Maps "Alidade Smooth" on our paid subscription. It's the same clean,
// muted, few-POI look the marker colours were designed against - Stadia
// recommends it for exactly this case (maps carrying a lot of markers and
// overlays). We moved here from CARTO Positron in August 2026, when CARTO
// started demanding an API key and served watermarked tiles under HTTP 200 to
// everyone who didn't have one.
//
// There is deliberately no API key anywhere in this codebase. Stadia
// authenticates by domain: it validates the browser's Origin/Referer against
// the Domain configured on a Property in the Stadia dashboard, which needs no
// key in the tile URL and no per-environment config. A Property's domain
// covers its subdomains, but a Property holds exactly one domain, so our hosts
// need three:
//
//   *.nrganext.nl     all tst/acc hosts for both brands, plus prd Stroomkaart
//   *.stroomkaart.nl  prd Stroomkaart, apex and www
//   *.nrgmaps.com     prd Bristol: bristol, west-of-england, www
//
// Two consequences worth knowing:
//
//   * A custom domain on a *new* apex needs a new Property, or its tiles come
//     back 401 and the map is blank. See the tileerror handler below.
//   * localhost / 127.0.0.1 are exempt entirely, so `npm run dev` needs no
//     setup. Those requests are rate-limited rather than metered.
//
// An API key would be the alternative, but it can't be scoped - the Domain
// field is a separate keyless auth path, not an allowlist over a key, so a key
// works from any origin and anyone who reads it out of a tile URL can spend
// our quota. Domains are strictly better for a browser app.
import L from 'leaflet';

// Swap to 'tiles-eu.stadiamaps.com' to pin tile requests inside the EU -
// same styles and URL shape, and worth considering given the customer base is
// Dutch and UK municipalities and port authorities.
const STADIA_HOST = 'tiles.stadiamaps.com';
const STADIA_STYLE = 'alidade_smooth';

// Stadia's terms require all three credits, with links, on every map - not
// just the OpenStreetMap one.
const STADIA_ATTRIBUTION =
  '&copy; <a href="https://stadiamaps.com/" target="_blank" rel="noopener">Stadia Maps</a> ' +
  '&copy; <a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

// Alidade Smooth has real tiles up to 20 (one better than CARTO gave us).
// Past that Leaflet upscales the level-20 tile so the map can keep zooming -
// slightly blurry, but lets address jumps and detail-zoom land at
// street/building level.
const MAX_NATIVE_ZOOM = 20;
const MAX_ZOOM = 22;

let reportedTileError = false;

/**
 * Builds the light base tile layer.
 *
 * Note `detectRetina` is deliberately left off. It would fetch @2x tiles on
 * high-DPI screens, which is sharper but multiplies our metered tile count for
 * a basemap that is intentionally low-detail. `{r}` stays in the template so
 * turning it on later is a one-option change.
 */
export function createBaseLayer(): L.TileLayer {
  const layer = L.tileLayer(
    `https://${STADIA_HOST}/tiles/${STADIA_STYLE}/{z}/{x}/{y}{r}.png`,
    {
      attribution: STADIA_ATTRIBUTION,
      maxNativeZoom: MAX_NATIVE_ZOOM,
      maxZoom: MAX_ZOOM,
    },
  );

  // Unlike CARTO's silent watermark, Stadia answers 401 - a real error status,
  // so this fires. Say why, once, instead of leaving a blank map unexplained.
  layer.on('tileerror', () => {
    if (reportedTileError) return;
    reportedTileError = true;
    console.error(
      `Basemap tiles failed to load from ${STADIA_HOST}. If these are 401s, no ` +
      `Stadia Property covers ${window.location.hostname} - add one for this ` +
      "apex domain in the Stadia dashboard (a Property's domain covers its subdomains).",
    );
  });

  return layer;
}

/** `createBaseLayer()` + `.addTo(map)`, for the maps with a single base layer. */
export function addBaseLayer(map: L.Map): L.TileLayer {
  return createBaseLayer().addTo(map);
}
